import React, {useEffect, useState} from 'react';
import {
    Collapse,
    Divider,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {useParams} from 'react-router';
import {useSession} from '../../../../firebase/UserProvider';
import EditableSection from "../../../../apps/policymaker/pages/readbooks/EditableSection";

const _awpBase = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
function _authH() { const t = localStorage.getItem('access_token'); return t ? {'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json'} : {'Content-Type': 'application/json'}; }

/** Inline multiline editable textarea — click to edit, blur/Ctrl+Enter to save. */
function InlineTextArea({value, onSave, placeholder}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? '');

    useEffect(() => { if (!editing) setDraft(value ?? ''); }, [value, editing]);

    const commit = () => {
        setEditing(false);
        if (draft.trim() !== (value ?? '').trim() && typeof onSave === 'function') onSave(draft.trim());
    };

    if (editing) {
        return (
            <TextField
                variant="outlined"
                size="small"
                autoFocus
                fullWidth
                multiline
                minRows={2}
                maxRows={8}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); }
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) commit();
                }}
                placeholder={placeholder}
                helperText="Ctrl+Enter to save · Esc to cancel"
                sx={{'& .MuiInputBase-root': {fontSize: '0.82rem'}}}
            />
        );
    }

    return (
        <Typography
            variant="body2"
            onClick={() => setEditing(true)}
            sx={{
                fontSize: '0.82rem',
                color: draft ? 'text.secondary' : 'text.disabled',
                cursor: 'text',
                lineHeight: 1.5,
                px: 0.25,
                py: 0.25,
                borderRadius: '4px',
                '&:hover': {bgcolor: 'action.hover'},
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
            }}
            title="Click to edit feed description"
        >
            {draft || placeholder}
        </Typography>
    );
}

const FeedHeader = ({title: titleProp, onTitleUpdated, unreadCount = 0, onFeedUpdated}) => {
    const {feedId} = useParams();
    const {user} = useSession() || {};

    const [feed, setFeed] = useState(null);
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(titleProp ?? '');
    const [settingsOpen, setSettingsOpen] = useState(false);

    useEffect(() => {
        if (!feedId) { setFeed(null); return; }
        let cancelled = false;
        fetch(`${_awpBase}/api/feeds/${encodeURIComponent(feedId)}`, {headers: _authH()})
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (!cancelled) setFeed(data); })
            .catch(err => { console.error('Failed to load feed:', err); if (!cancelled) setFeed(null); });
        return () => { cancelled = true; };
    }, [feedId]);

    useEffect(() => {
        if (!editing) setValue((feed && feed.title) || (titleProp ?? ''));
    }, [editing, feed, titleProp]);

    const handleFieldSave = async (fieldKey, next) => {
        if (!feedId) return;
        setFeed(f => ({...(f || {}), [fieldKey]: next}));
        try {
            await fetch(`${_awpBase}/api/feeds/${encodeURIComponent(feedId)}`, {
                method: 'PATCH', headers: _authH(), body: JSON.stringify({[fieldKey]: next}),
            });
            if (typeof onFeedUpdated === 'function') onFeedUpdated();
        } catch (err) {
            console.error(`Failed to update feed field ${fieldKey}:`, err);
        }
    };

    const commit = async () => {
        const next = (value ?? '').toString();
        const current = (feed && feed.title) || (titleProp ?? '');
        setEditing(false);
        if (next !== current && feedId) {
            if (typeof onTitleUpdated === 'function') onTitleUpdated(next);
            await handleFieldSave('title', next);
        }
    };

    const title = (feed && feed.title) || (titleProp ?? 'Untitled feed');
    const description = (feed && feed.description) || '';

    return (
        <Stack sx={{bgcolor: 'white'}}>
            {/* Title bar */}
            <Stack direction="row" alignItems="center" spacing={1} px={2} py={1}>
                <Stack flexGrow={1} minWidth={0}>
                    {editing ? (
                        <TextField
                            variant="standard"
                            autoFocus
                            fullWidth
                            size="small"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            onBlur={commit}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') commit();
                                if (e.key === 'Escape') { setValue(title); setEditing(false); }
                            }}
                            placeholder="Feed title"
                        />
                    ) : (
                        <Stack direction="row" alignItems="baseline" spacing={1}>
                            <Typography
                                variant="subtitle1"
                                fontWeight={700}
                                onClick={user ? () => setEditing(true) : undefined}
                                sx={{cursor: user ? 'text' : 'default', '&:hover': user ? {bgcolor: 'action.hover'} : undefined, borderRadius: '4px', px: 0.25}}
                                title={user ? 'Click to rename' : undefined}
                            >
                                {title}
                            </Typography>
                            {unreadCount > 0 && (
                                <Typography variant="caption" color="primary.main" fontWeight={600}>
                                    {unreadCount} unread
                                </Typography>
                            )}
                        </Stack>
                    )}
                </Stack>

                <Tooltip title={settingsOpen ? 'Hide settings' : 'Feed settings'}>
                    <IconButton size="small" onClick={() => setSettingsOpen(s => !s)}>
                        {settingsOpen ? <ExpandLessIcon fontSize="small"/> : <TuneRoundedIcon fontSize="small"/>}
                    </IconButton>
                </Tooltip>
            </Stack>

            {/* Description — the text used for LLM rephrase + vector search */}
            <Stack px={2} pb={1} sx={{borderBottom: '1px dashed #e8eaf0'}}>
                <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                    <Tooltip title="This description is rephrased by AI and used to find semantically matching FINTEL reports" placement="right" arrow>
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: '0.6rem',
                                fontWeight: 700,
                                color: 'text.disabled',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                pt: 0.5,
                                flexShrink: 0,
                                cursor: 'help',
                            }}
                        >
                            Query
                        </Typography>
                    </Tooltip>
                    <Stack flexGrow={1} minWidth={0}>
                        <InlineTextArea
                            value={description}
                            onSave={(next) => handleFieldSave('description', next)}
                            placeholder="Describe what this feed tracks — click to edit…"
                        />
                    </Stack>
                </Stack>
            </Stack>

            {/* Collapsible settings panel */}
            <Collapse in={settingsOpen}>
                <Stack px={2} pb={2} pt={0.5} spacing={1} bgcolor="#fafafa" borderTop="1px solid #f0f0f0">
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{textTransform: 'uppercase', letterSpacing: '0.06em'}}>
                        Feed filters
                    </Typography>
                    <EditableSection title="Sources" items={(feed && feed.sources) || []} fieldKey="sources" feedId={feedId} onSaved={(next) => handleFieldSave('sources', next)}/>
                    <EditableSection title="Entities" items={(feed && feed.entities) || []} fieldKey="entities" feedId={feedId} onSaved={(next) => handleFieldSave('entities', next)}/>
                    <EditableSection title="Indicators" items={(feed && feed.indicators) || []} fieldKey="indicators" feedId={feedId} onSaved={(next) => handleFieldSave('indicators', next)}/>
                    <EditableSection title="Triggers" items={(feed && feed.triggers) || []} fieldKey="triggers" feedId={feedId} onSaved={(next) => handleFieldSave('triggers', next)}/>
                </Stack>
            </Collapse>

            <Divider sx={{borderColor: '#e8eaf0'}}/>
        </Stack>
    );
};

export default FeedHeader;
